import React from "react";
import PropTypes from "prop-types";
import {
    Card,
    CardHead,
    KeyValue,
    Pill,
    SectionHeading,
    EmptyState,
    StatusBadge,
    Icon,
    ICONS,
    EmbeddedScreen
} from "./ui";
import {
    getCategoryDisplayLabel,
    getCategorySubtitle,
    getDesignSectionTypes,
    findResultForType,
    toPlainJs
} from "utilities/nach-mandate-utils";
import { BANK_ACCOUNT_TYPES } from "constants/enums";
import NachMandate from "../nach-mandate";
import { enumLabel, get, getIn, money, date } from "./data-utils";

/**
 * NACH mandate.
 *
 * The top of the panel is the workspace's own read of `/enach-status/`: what
 * the case needs, what is registered against which account, and what is still
 * holding the proceed gate shut. Below it sits the mandate registration screen
 * itself — triggering a mandate means the Digio hand-off, physical mandate and
 * PDC uploads, cancellation and per-mode status polling, so it runs here
 * rather than being reimplemented and allowed to drift.
 *
 * That screen reads the case off the store by id: without caseId (and the case
 * record the mandate forms price off) it mounts against an empty slice, which
 * is why the embed is held until both are in hand.
 */

const MODE_ORDER = ["API", "PHYSICAL", "ESIGN", "DEBIT_CARD"];

/**
 * Where a mandate stands, as one of three states an operator reads at a
 * glance. NACH_STATUS: 4 Verified and 5 Bank Accepted are done; 1 Open,
 * 2 Closed and 3 Failed are in flight; nothing at all means not started.
 */
const DONE_STATUSES = [4, 5];
const ONGOING_STATUSES = [1, 2, 3];

const summaryState = code => {
    if (DONE_STATUSES.includes(Number(code))) {
        return { key: "done", label: "Completed", tone: "ok" };
    }
    if (ONGOING_STATUSES.includes(Number(code))) {
        return { key: "ongoing", label: "Ongoing", tone: "warn" };
    }
    return { key: "idle", label: "Not started", tone: "info" };
};

/**
 * The status code lives on the mandate attempt (`nach_details.nach_status`);
 * the journey's own per-mode flags are the fallback for a category the results
 * list has not caught up with.
 */
const statusCodeFor = (category, resultRow) => {
    const fromResult = resultRow && resultRow.nach_details
        ? resultRow.nach_details.nach_status
        : null;
    if (fromResult !== null && fromResult !== undefined) return fromResult;
    const modes = get(category, "modes") || {};
    const codes = MODE_ORDER.map(key => modes[key])
        .filter(Boolean)
        .map(mode => get(mode, "nach_status"))
        .filter(code => code !== null && code !== undefined);
    if (codes.some(code => DONE_STATUSES.includes(Number(code)))) return 4;
    if (codes.length) return codes[0];
    // No code anywhere: fall back to the journey's own success flag.
    return MODE_ORDER.map(key => modes[key]).some(mode => mode && get(mode, "is_success"))
        ? 4
        : null;
};

const modeRows = category => {
    const modes = get(category, "modes") || {};
    return MODE_ORDER.filter(key => modes[key]).map(key => {
        const mode = modes[key];
        return {
            key,
            label: key.replace("_", " "),
            status: get(mode, "status_label") || "Not started",
            umrn: get(mode, "umrn") || get(mode, "mandate_id"),
            ok: !!get(mode, "is_success")
        };
    });
};

const NachSection = ({
    nachStatus,
    bankAccounts,
    caseId,
    companyId,
    schemeType,
    caseDetail
}) => {
    const journey = toPlainJs(get(nachStatus, "nach_journey")) || null;
    const proceed = toPlainJs(get(nachStatus, "proceed_validation")) || {};
    const requiredTypes = toPlainJs(get(nachStatus, "required_nach_types")) || [];
    const bankAccount = toPlainJs(get(nachStatus, "bank_account")) || {};
    const emiDates = toPlainJs(get(nachStatus, "emi_dates")) || [];
    const results = (toPlainJs(get(nachStatus, "data")) || []).map(row => toPlainJs(row));
    const categories = (journey && journey.categories) || {};
    const blockers = proceed.blockers || [];
    const instalmentFrequency =
        get(nachStatus, "instalment_frequency") !== null &&
        get(nachStatus, "instalment_frequency") !== undefined
            ? get(nachStatus, "instalment_frequency")
            : journey && journey.instalment_frequency;

    const keyAccount =
        bankAccounts.find(account => account.isKey) || bankAccounts[0] || null;

    // The mandates this case actually needs, in the order the registration
    // screen renders them.
    const applicable = journey
        ? getDesignSectionTypes(journey, requiredTypes, instalmentFrequency)
        : requiredTypes;

    const summary = applicable.map(type => {
        const category = categories[type];
        const resultRow = findResultForType(results, type, null);
        const state = summaryState(statusCodeFor(category, resultRow));
        return {
            type,
            label: getCategoryDisplayLabel(type).replace("E-NACH — ", ""),
            sub: getCategorySubtitle(type, journey, emiDates, category),
            state
        };
    });

    const done = summary.filter(item => item.state.key === "done").length;
    const emiSections = summary.filter(item => item.type !== "SECURITY");
    const otherSections = summary.filter(item => item.type === "SECURITY");

    const mandateCard = item => {
        const category = categories[item.type];
        const rows = modeRows(category);
        return (
            <Card key={item.type}>
                <CardHead
                    title={getCategoryDisplayLabel(item.type)}
                    sub={item.sub}
                    extra={<Pill tone={item.state.tone}>{item.state.label}</Pill>}
                />
                {rows.length ? (
                    rows.map(row => (
                        <div key={row.key} className={"ops-ws__row ops-ws__row--alt"}>
                            <span className={"ops-ws__row-label"}>{row.label}</span>
                            <span
                                style={{
                                    flex: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10
                                }}
                            >
                                {row.umrn ? (
                                    <span
                                        className={"ops-ws__kv-value--muted"}
                                        style={{ fontSize: 12 }}
                                    >
                                        {row.umrn}
                                    </span>
                                ) : null}
                                <Pill tone={row.ok ? "ok" : "warn"}>{row.status}</Pill>
                            </span>
                        </div>
                    ))
                ) : (
                    <div className={"ops-ws__card-body"}>
                        <div className={"ops-ws__note"}>
                            {"No attempt recorded for this mandate yet — register it below."}
                        </div>
                    </div>
                )}
            </Card>
        );
    };

    return (
        <div className={"ops-ws__section"}>
            <SectionHeading
                title={"NACH mandate"}
                subtitle={
                    "Mandate registration against the key bank account, plus the security mandates the case requires."
                }
                extra={
                    <Pill tone={proceed.can_proceed ? "ok" : "warn"}>
                        {proceed.can_proceed
                            ? "Ready to proceed"
                            : `${blockers.length} blocker${blockers.length === 1 ? "" : "s"}`}
                    </Pill>
                }
            />

            {/*
              * Applicable NACH, colour-coded: green once the mandate is
              * verified or bank-accepted (NACH_STATUS 4, 5), amber while an
              * attempt is in flight (1, 2, 3), blue when none has been made.
              */}
            <Card>
                <CardHead
                    title={"Applicable NACH"}
                    sub={
                        proceed.can_proceed && proceed.is_ready_message
                            ? proceed.is_ready_message
                            : "Every mandate here has to be registered before the PS Kit gate opens."
                    }
                    extra={
                        <Pill tone={done === summary.length && summary.length ? "ok" : "warn"}>
                            {summary.length ? `${done} / ${summary.length} completed` : "—"}
                        </Pill>
                    }
                />
                {summary.length ? (
                    <div className={"ops-ws__nach-summary"}>
                        {summary.map(item => (
                            <div
                                key={item.type}
                                className={`ops-ws__nach-tile is-${item.state.key}`}
                            >
                                <span className={"ops-ws__nach-tile-label"}>{item.label}</span>
                                <span className={"ops-ws__nach-tile-state"}>
                                    {item.state.label}
                                </span>
                                {item.sub ? (
                                    <span className={"ops-ws__nach-tile-sub"}>{item.sub}</span>
                                ) : null}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={"ops-ws__card-body"}>
                        <EmptyState>
                            {"No NACH mandate is required for this case, or the status has not loaded yet."}
                        </EmptyState>
                    </div>
                )}
            </Card>

            {blockers.length ? (
                <Card>
                    <CardHead
                        title={"Blocking the proceed gate"}
                        extra={<Pill tone={"warn"}>{`${blockers.length} open`}</Pill>}
                    />
                    <div className={"ops-ws__card-body ops-ws__check-list"}>
                        {blockers.map(blocker => (
                            <div
                                key={`${blocker.nach_request_type}-${blocker.mandate_mode}`}
                                className={"ops-ws__check-row"}
                            >
                                <StatusBadge tone={"warn"} small />
                                <span>{blocker.message}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            ) : null}

            {/*
              * The account the mandates are registered against leads the left
              * column; EMI 1 sits under it with Security beside it, and EMI 2
              * under EMI 1.
              */}
            <div
                className={
                    "ops-ws__nach-grid" +
                    (emiSections.length ? "" : " is-security-only")
                }
            >
                <div className={"ops-ws__nach-account"}>
                    <Card>
                        <CardHead
                            title={"Registered account"}
                            extra={<Icon d={ICONS.bank} size={16} stroke={"#98a2b3"} />}
                        />
                        <div className={"ops-ws__card-body"}>
                            <div className={"ops-ws__kv-grid"}>
                                <KeyValue
                                    label={"Account"}
                                    value={
                                        get(bankAccount, "account_number") ||
                                        (keyAccount && keyAccount.number)
                                    }
                                />
                                <KeyValue
                                    label={"Holder"}
                                    value={
                                        get(bankAccount, "account_holder_name") ||
                                        (keyAccount && keyAccount.holder)
                                    }
                                />
                                <KeyValue
                                    label={"IFSC"}
                                    value={
                                        getIn(bankAccount, ["bank_branch", "ifsc_code"]) ||
                                        (keyAccount && keyAccount.ifsc)
                                    }
                                />
                                <KeyValue
                                    label={"Bank"}
                                    value={
                                        getIn(bankAccount, ["bank_branch", "bank_name"]) ||
                                        (keyAccount && keyAccount.bank)
                                    }
                                />
                                <KeyValue
                                    label={"Type"}
                                    value={
                                        get(bankAccount, "account_type")
                                            ? enumLabel(
                                                BANK_ACCOUNT_TYPES,
                                                get(bankAccount, "account_type")
                                            )
                                            : keyAccount && keyAccount.typeLabel
                                    }
                                />
                                <KeyValue
                                    label={"Limit"}
                                    value={
                                        get(bankAccount, "account_limit")
                                            ? money(get(bankAccount, "account_limit"))
                                            : keyAccount && keyAccount.limit
                                                ? money(keyAccount.limit)
                                                : null
                                    }
                                />
                                {emiDates.length ? (
                                    <KeyValue
                                        label={"EMI dates"}
                                        value={emiDates.map(value => date(value)).join(", ")}
                                    />
                                ) : journey && journey.emi_date ? (
                                    <KeyValue
                                        label={"EMI date"}
                                        value={date(journey.emi_date)}
                                    />
                                ) : null}
                                <KeyValue
                                    label={"Product"}
                                    value={
                                        journey && journey.is_term_loan
                                            ? "Term Loan"
                                            : schemeType || null
                                    }
                                    tone={"muted"}
                                />
                            </div>
                        </div>
                    </Card>
                </div>

                {emiSections.map(item => (
                    <div key={item.type} className={"ops-ws__nach-emi"}>
                        {mandateCard(item)}
                    </div>
                ))}

                {otherSections.map(item => (
                    <div key={item.type} className={"ops-ws__nach-security"}>
                        {mandateCard(item)}
                    </div>
                ))}
            </div>

            <EmbeddedScreen
                title={"Mandate registration"}
                note={
                    "Trigger a mandate, upload a physical mandate or PDC set, and track each attempt. This is the NACH Mandate screen itself, so every action behaves exactly as it does on its own tab."
                }
                ready={Boolean(caseId && caseDetail && caseDetail.get)}
            >
                <NachMandate
                    caseId={caseId}
                    companyId={companyId}
                    schemeType={schemeType}
                    caseDetail={caseDetail}
                />
            </EmbeddedScreen>
        </div>
    );
};

NachSection.defaultProps = { bankAccounts: [] };

NachSection.propTypes = {
    nachStatus: PropTypes.object,
    bankAccounts: PropTypes.array.isRequired,
    caseId: PropTypes.number,
    companyId: PropTypes.number,
    schemeType: PropTypes.string,
    caseDetail: PropTypes.object
};

export default NachSection;
